import type { InspectionItem, InspectionStage } from './types';

export const INSPECTION_ITEMS: InspectionItem[] = [
  { id: 'a', code: 'FRONT DOOR R', label: 'Front Door - Right' },
  { id: 'b', code: 'FRONT DOOR L', label: 'Front Door - Left' },
  { id: 'c', code: 'REAR DOOR R', label: 'Rear Door - Right' },
  { id: 'd', code: 'REAR DOOR L', label: 'Rear Door - Left' },
  { id: 'e', code: 'TAILGATE', label: 'Tailgate' },
  { id: 'f', code: 'HOOD', label: 'Hood' },
  { id: 'g', code: 'FENDER R', label: 'Fender - Right' },
  { id: 'h', code: 'FENDER L', label: 'Fender - Left' },
  { id: 'i', code: 'BODY/ROOF', label: 'Body / Roof / Others' },
  { id: 'j', code: 'CASE COND.', label: 'Case Condition' },
  { id: 'k', code: 'WOOD PALLET', label: 'Wood Pallet (x16cm)', ruleNote: 'Excluded in Receiving & Delivery' },
  { id: 'l', code: 'SKID COND.', label: 'Skid Condition', ruleNote: 'Only in Delivery Stage' },
];

export const INSPECTION_STAGES: InspectionStage[] = [
  { id: 1, name: 'Section 1: INCOMING RECEIVING', shortName: '1. Receiving', defaultInspector: '' },
  { id: 2, name: 'Section 2: AFTER UNLOADING', shortName: '2. Unloading', defaultInspector: '' },
  { id: 3, name: 'Section 3: AFTER UNSTUFFING', shortName: '3. Unstuffing', defaultInspector: '' },
  { id: 4, name: 'Section 4: STORAGE', shortName: '4. Storage', defaultInspector: '' },
  { id: 5, name: 'Section 5: DELIVERY', shortName: '5. Delivery', defaultInspector: '' },
];

export const DEFECT_TYPES = ['Hump', 'Scratch', 'Paint Wet', 'Dent', 'Broken', 'Missing', 'Others'] as const;

export const DEFAULT_INVOICE_MAPPINGS = [
  { invoiceNo: '408', bodies: ['TY273393', 'TY273394', 'TY273395', 'TY273396', 'TY273397'] },
  { invoiceNo: '409', bodies: ['TY273400', 'TY273401', 'TY273402', 'TY273403'] },
  { invoiceNo: '410', bodies: ['TY273410', 'TY273411', 'TY273412'] },
];

export const DEFAULT_STANDALONE_CHOICES = {
  cases: ['10', '11', '12', '13'],
  boxes: ['408', '409', '410', '411'],
};

export function isItemApplicable(itemId: string, stageId: number): boolean {
  if (itemId === 'k' && (stageId === 1 || stageId === 5)) return false;
  if (itemId === 'l' && stageId !== 5) return false;
  return true;
}

export function initStageState(): Record<number, import('./types').StageState> {
  const now = new Date();
  const dateStr = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear().toString().slice(-2)}`;
  const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  const stages: Record<number, import('./types').StageState> = {};

  for (const stage of INSPECTION_STAGES) {
    const items: Record<string, import('./types').StageItemState> = {};

    for (const item of INSPECTION_ITEMS) {
      const applicable = isItemApplicable(item.id, stage.id);
      items[item.id] = {
        status: applicable ? 'OK' : 'NA',
        defectType: '',
        remark: '',
      };
    }

    stages[stage.id] = {
      dateTime: `${dateStr} ${timeStr}`,
      inspectorFure: stage.defaultInspector,
      actionStatus: 'SKD Body OK',
      saved: false,
      savedAt: null,
      savedBy: null,
      items,
    };
  }

  return stages;
}

export function computeAggregation(stages: Record<number, import('./types').StageState>) {
  let totalChecked = 0;
  let passCount = 0;
  let defectCount = 0;

  for (const stage of INSPECTION_STAGES) {
    const stageData = stages[stage.id];
    if (!stageData) continue;

    for (const item of INSPECTION_ITEMS) {
      if (!isItemApplicable(item.id, stage.id)) continue;
      const itemData = stageData.items[item.id];
      if (!itemData) continue;

      totalChecked++;
      if (itemData.status === 'OK') passCount++;
      else if (itemData.status === 'NG') defectCount++;
    }
  }

  const passRate = totalChecked > 0
    ? parseFloat(((passCount / totalChecked) * 100).toFixed(1))
    : 100.0;

  const verdict = defectCount === 0
    ? 'PASSED (100% OK)'
    : 'ACTION REQUIRED (NG)';

  return { totalChecked, passCount, defectCount, passRate, verdict };
}
